import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCardListStatus } from '@/hooks'
import { APIWeb } from '@/api/index'
import RefreshList from '@/components/RefreshList'
import Ripple from '@/components/Ripple'
import EmptyState from '@/components/EmptyState'
import SkeletonBox from '@/components/SkeletonBox'
import { useTransactionStatus } from '@/hooks'
import { formatDateTime, formatThousands } from '@/utils'
import { getTransactionIcon } from '../../utils'


// 交易类型枚举
export enum TransactionType {
  Transfer = 'transfer',    // 转入/汇入
  Consume = 'consume',      // 消费
  Refund = 'refund',        // 退款
  Withdraw = 'withdraw',    // 提现
  Other = 'other'           // 其他
}

// 卡片信息接口
export interface CardInfo {
  cardImage?: string
  cardNo?: string
  cardStatus?: string
  cardType?: string
}

export interface TransactionRecordsProps {
  /** 卡片ID */
  cardId: number
  /** 卡片信息（用于跳转更多页面时展示） */
  cardInfo?: CardInfo
  /** 自定义类名 */
  className?: string
}

const TransactionRecords: React.FC<TransactionRecordsProps> = ({ 
  cardId,
  cardInfo,
  className = '' 
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getCardListStatus } = useCardListStatus()
  const [data, setData] = useState<getUserCardConsumptionRecordListItemResp[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const { getTransactionStatus } = useTransactionStatus()

  const handleRecordClick = useCallback((record: getUserCardConsumptionRecordListItemResp) => {
    navigate('/card-v2/consumption-detail', { state: { record } })
  }, [navigate])

  const handleMoreClick = useCallback(() => {
    navigate(`/card-v2/consumption-records?cardId=${cardId}`, {
      state: cardInfo
    })
  }, [navigate, cardId, cardInfo])

  const fetchConsumptionRecords = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading || !cardId) return
    setLoading(true)
    
    try {
      const response = await APIWeb.getUserCardConsumptionRecordV2({ card_id: cardId, page, page_size: pageSize })
      if (response?.list) {
        const newData = response.list
        if (isAppend) {
          setData(prevData => [...prevData, ...newData])
        } else {
          setData(newData)
        }
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setCurrentPage(page + 1)
          setHasMore(true)
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取卡片消费记录失败:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [cardId, loading])

  // 当卡片ID变化时，重新加载数据
  useEffect(() => {
    if (cardId) {
      setData([])
      setCurrentPage(1)
      setHasMore(true)
      fetchConsumptionRecords(1, false)
    }
  }, [cardId])

  const loadMore = useCallback(async () => {
    await fetchConsumptionRecords(currentPage, true)
  }, [fetchConsumptionRecords, currentPage])

  const onRefresh = useCallback(async () => {
    await fetchConsumptionRecords(1, false)
  }, [fetchConsumptionRecords])

  // 骨架屏
  const renderRecordSkeleton = useCallback(() => {
    return (
      <div className="flex items-center py-4">
        {/* 左侧图标骨架 */}
        <SkeletonBox className="w-10 h-10 rounded-full flex-shrink-0" />

        {/* 中间内容骨架 */}
        <div className="flex-1 ml-3">
          <SkeletonBox className="w-24 h-4 rounded mb-2" />
          <SkeletonBox className="w-32 h-3 rounded" />
        </div>

        {/* 右侧金额骨架 */}
        <div className="text-right">
          <SkeletonBox className="w-20 h-4 rounded mb-2" />
          <SkeletonBox className="w-12 h-3 rounded ml-auto" />
        </div>
      </div>
    )
  }, [])

  // 渲染单条记录
  const renderRecordItem = useCallback((record: getUserCardConsumptionRecordListItemResp) => {
    const icon = getTransactionIcon(record.txn_type)

    return (
      <Ripple className="rounded-xl">
        <div 
          className="flex items-center py-4"
          onClick={() => handleRecordClick(record)}
        >
          {/* 左侧图标 */}
          <div className="w-10 h-10 rounded-full bg-[#F2F4F7] flex items-center justify-center flex-shrink-0">
            <img src={icon} alt="" className="w-5 h-5" />
          </div>

          {/* 中间内容 */}
          <div className="flex-1 ml-3 min-w-0">
            <div className="text-sm font-medium text-[#1D2129] truncate">
              {getTransactionStatus(record.txn_type || '--', undefined, record.txn_type_string)}
            </div>
            <div className="text-xs text-[#86909C] mt-1">
              {formatDateTime(record.transaction_time)}
            </div>
          </div>

          {/* 右侧金额和状态 */}
          <div className="text-right flex-shrink-0 ml-3">
            <div className={`text-sm`}>
            {record.total_amt != null && record.total_amt >= 0 ? '+' : '-'}${formatThousands(Math.abs(record.total_amt || 0), 2, true)}
            </div>
            <div className="text-xs text-[#86909C] mt-1">
              {t('已完成')}
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [getCardListStatus, handleRecordClick, t])

  return (
    <div className={`px-5 ${className}`}>
      {/* 标题栏 */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-semibold text-[#1D2129]">{t('交易记录')}</span>
        {data.length > 0 && (
          <span 
            className="text-sm text-[#165DFF] cursor-pointer"
            onClick={handleMoreClick}
          >
            {t('更多')}
          </span>
        )}
      </div>

      {/* 记录列表 */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="max-h-96 min-h-48 flex flex-col" style={{ touchAction: 'pan-y' }}>
          <RefreshList
            data={data}
            renderItem={renderRecordItem}
            keyExtractor={(item, index) => `${item.id}-${index}`}
            loading={loading}
            hasMore={hasMore}
            onRefresh={onRefresh}
            onLoadMore={loadMore}
            renderSkeleton={renderRecordSkeleton}
            skeletonCount={5}
            emptyComponent={
              <EmptyState 
                text={t('暂无记录')} 
                icon={<img src="/images/card-v2/card-no-records.svg" alt="" className="w-32 h-auto" />}
              />
            }
            finishedText={t('没有更多数据了')}
            loadingText={t('加载中')}
            className="flex-1 overflow-y-auto scrollbar-hide"
            disableAutoLoad
          />
        </div>
      </div>
    </div>
  )
}

export default TransactionRecords
